// ---------------------------------------------------------------------------
// SeedToTenantModal — Modal for selecting tenants to seed a system template to
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { X, Search, Building2, Check, AlertCircle, Loader2 } from 'lucide-react';
import { listTenants } from '../services/tenantService';

interface Tenant {
  _id: string;
  name: string;
  slug?: string;
  status: string;
  tier?: string;
}

interface SeedToTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSeed: (tenantIds: string[]) => Promise<{ success: boolean; seededCount: number }>;
  templateName: string;
  templateType: 'report' | 'dashboard' | 'document';
}

export default function SeedToTenantModal({
  isOpen,
  onClose,
  onSeed,
  templateName,
  templateType,
}: SeedToTenantModalProps) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState<{ success: boolean; seededCount: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTenants();
      setSelectedIds(new Set());
      setSearchQuery('');
      setResult(null);
    }
  }, [isOpen]);

  async function loadTenants() {
    setLoading(true);
    try {
      const data = await listTenants();
      // Filter to only active tenants
      const activeTenants = data.filter((t: Tenant) => t.status === 'active');
      setTenants(activeTenants);
    } catch (error) {
      console.error('[SeedToTenantModal] Failed to load tenants:', error);
    } finally {
      setLoading(false);
    }
  }

  const filteredTenants = tenants.filter((t) =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.slug && t.slug.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const toggleTenant = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    setSelectedIds(new Set(filteredTenants.map((t) => t._id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleSeed = async () => {
    if (selectedIds.size === 0) return;
    setSeeding(true);
    setResult(null);
    try {
      const res = await onSeed(Array.from(selectedIds));
      setResult(res);
      if (res.success) {
        // Auto-close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('[SeedToTenantModal] Seed failed:', error);
      setResult({ success: false, seededCount: 0 });
    } finally {
      setSeeding(false);
    }
  };

  if (!isOpen) return null;

  const typeLabel = templateType === 'report' ? 'report template' : templateType === 'dashboard' ? 'dashboard template' : 'document template';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Seed to Departments</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Copy "{templateName}" to selected departments
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search departments..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">
              {selectedIds.size} of {filteredTenants.length} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={deselectAll}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Tenant List */}
        <div className="flex-1 overflow-auto px-6 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No departments found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredTenants.map((tenant) => (
                <label
                  key={tenant._id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                    selectedIds.has(tenant._id)
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(tenant._id)}
                    onChange={() => toggleTenant(tenant._id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{tenant.name}</p>
                    {tenant.slug && (
                      <p className="text-xs text-gray-500 truncate">{tenant.slug}</p>
                    )}
                  </div>
                  {tenant.tier && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                      {tenant.tier}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Result Message */}
        {result && (
          <div
            className={`mx-6 mb-3 px-4 py-3 rounded-lg flex items-center gap-2 ${
              result.success
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {result.success ? (
              <>
                <Check className="h-4 w-4" />
                <span className="text-sm">
                  Successfully seeded to {result.seededCount} department{result.seededCount !== 1 ? 's' : ''}
                </span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Failed to seed template. Please try again.</span>
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSeed}
            disabled={selectedIds.size === 0 || seeding}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {seeding ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Seeding...
              </>
            ) : (
              <>
                Seed to {selectedIds.size} Department{selectedIds.size !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
